import type {
  ConditionDto,
  EditActionDto,
  SourceEntity,
  GroupEntity,
  CategoryEntity,
  ExpenseTypeEntity,
} from "../api/types";
import { isZero } from "./money";
import type { StoredTransaction } from "./derivedAccount";

// Helper to create a default empty condition
export const createDefaultCondition = (): ConditionDto => ({
  type: "field",
  field: "description",
  operator: "contains",
  value: "",
});

// Resolves transaction names to registry ids when seeding the form's actions.
// Optional — when omitted (e.g. registry not yet loaded) the action list is
// returned empty rather than holding stale string values.
export interface RegistryLookups {
  sourceByName: Record<string, SourceEntity | undefined>;
  groupByName: Record<string, GroupEntity | undefined>;
  categoryByName: Record<string, CategoryEntity | undefined>;
  expenseTypeByName: Record<string, ExpenseTypeEntity | undefined>;
}

export const getInitialStateFromTransaction = (
  transaction?: StoredTransaction,
  lookups?: RegistryLookups,
): {
  name: string;
  condition: ConditionDto;
  actions: EditActionDto[];
} => {
  if (!transaction) {
    return {
      name: "",
      condition: createDefaultCondition(),
      actions: [],
    };
  }

  const name = transaction.merchant || "";
  const newConditions: ConditionDto[] = [];

  // The registry-backed fields seed as "is <that entry>", by id, so a rule
  // made from a transaction survives a later rename. Without the registry
  // there is no id to name, so those fields fall back to the text they came
  // with — the legacy comparison, which is still what the rule means.
  const entityCondition = (
    field: "source" | "group" | "expenseType",
    entity: { id: string } | undefined,
    text: string,
  ): ConditionDto =>
    entity
      ? { type: "field", field, operator: "is", value: entity.id }
      : { type: "field", field, operator: "equal", value: text };

  if (transaction.source) {
    newConditions.push(
      entityCondition(
        "source",
        lookups?.sourceByName[transaction.source],
        transaction.source,
      ),
    );
  }
  if (transaction.merchant) {
    newConditions.push({
      type: "field",
      field: "merchant",
      operator: "equal",
      value: transaction.merchant,
    });
  }
  if (transaction.description) {
    newConditions.push({
      type: "field",
      field: "description",
      operator: "equal",
      value: transaction.description,
    });
  }
  if (transaction.group) {
    newConditions.push(
      entityCondition(
        "group",
        lookups?.groupByName[transaction.group],
        transaction.group,
      ),
    );
  }
  if (transaction.expenseType) {
    newConditions.push(
      entityCondition(
        "expenseType",
        lookups?.expenseTypeByName[transaction.expenseType],
        transaction.expenseType,
      ),
    );
  }
  if (!isZero(transaction.amount)) {
    newConditions.push({
      type: "field",
      field: "amount",
      operator: "equal",
      value: transaction.amount.amount,
    });
  }

  const condition: ConditionDto =
    newConditions.length > 0
      ? { type: "all", conditions: newConditions }
      : createDefaultCondition();

  const newActions: EditActionDto[] = [];
  if (lookups) {
    const groupId = transaction.group
      ? lookups.groupByName[transaction.group]?.id
      : undefined;
    if (groupId) {
      newActions.push({ field: "group", value: groupId });
    }

    const expenseTypeId = transaction.expenseType
      ? lookups.expenseTypeByName[transaction.expenseType]?.id
      : undefined;
    if (expenseTypeId) {
      newActions.push({ field: "expenseType", value: expenseTypeId });
    }

    const categoryIds = (transaction.categories ?? [])
      .map((cat) => lookups.categoryByName[cat]?.id)
      .filter((id) => id !== undefined);
    if (categoryIds.length > 0) {
      newActions.push({ field: "addCategory", value: categoryIds });
    }
  }

  if (transaction.description) {
    newActions.push({
      field: "description",
      value: transaction.description,
    });
  } else if (transaction.merchant) {
    newActions.push({ field: "description", value: transaction.merchant });
  }

  return { name, condition, actions: newActions };
};
